import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plane, Building2, Globe, FileText, MapPin, LayoutGrid, Compass, Lightbulb } from "lucide-react";
import api from "../lib/api";

export default function SearchBar({ compact = false }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search`, { params: { q } });
        setRes(data);
        setOpen(true);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); nav(path); };

  const runNow = async () => {
    if (q.trim().length < 2) return;
    try {
      const { data } = await api.get(`/search`, { params: { q } });
      setRes(data);
      setOpen(true);
    } catch {}
  };

  return (
    <div className="relative w-full" ref={ref}>
      <div className="flex items-center border border-zinc-300 bg-white focus-within:ring-2 focus-within:ring-primary transition-colors">
        <Search className="w-4 h-4 ml-3 text-muted-foreground shrink-0" />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => res && setOpen(true)}
          onKeyDown={(e) => e.key === "Enter" && runNow()}
          placeholder="Search anything — airports, airlines, articles, features…"
          className={`w-full bg-transparent outline-none px-3 ${compact ? "py-2 text-sm" : "py-3.5 text-base"}`}
        />
        <button onClick={runNow} data-testid="global-search-go" aria-label="Search"
          className={`shrink-0 mr-1 px-3 ${compact ? "py-1" : "py-1.5"} bg-primary text-white text-xs font-800 hover:opacity-90`}>
          Go&nbsp;&rsaquo;
        </button>
      </div>
      {open && res && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-zinc-300 shadow-xl max-h-96 overflow-auto" data-testid="search-results">
          {res.pages?.length > 0 && <Section label="On TravelerPulse" />}
          {res.pages?.map((p) => (
            <Row key={p.path + p.title} icon={<LayoutGrid className="w-4 h-4 text-primary" />}
              onClick={() => go(p.path)} main={p.title} sub={p.sub} />
          ))}
          {res.airports?.length > 0 && <Section label="Airports" />}
          {res.airports?.map((a) => (
            <Row key={a.id} icon={<Building2 className="w-4 h-4 text-primary" />}
              onClick={() => go(`/airports/${a.iata_code || a.id}`)}
              main={a.name} sub={`${a.iata_code || "—"} · ${a.municipality || ""} ${a.iso_country || ""}`} />
          ))}
          {res.airlines?.length > 0 && <Section label="Airlines" />}
          {res.airlines?.map((a) => (
            <Row key={a.id} icon={<Plane className="w-4 h-4 text-primary" />}
              onClick={() => go(`/airlines/${a.id}`)}
              main={a.name} sub={`${a.iata || "—"} · ${a.country || ""}`} />
          ))}
          {res.countries?.length > 0 && <Section label="Countries" />}
          {res.countries?.map((c) => (
            <Row key={c.id} icon={<Globe className="w-4 h-4 text-primary" />}
              onClick={() => go(`/countries/${c.id}`)}
              main={c.name} sub={c.continent} />
          ))}
          {res.destinations?.length > 0 && <Section label="Destinations" />}
          {res.destinations?.map((d) => (
            <Row key={d.id} icon={<MapPin className="w-4 h-4 text-primary" />}
              onClick={() => go(`/destinations/${d.id}`)}
              main={d.name} sub={d.country} />
          ))}
          {res.guides?.length > 0 && <Section label="Destination guides" />}
          {res.guides?.map((g) => (
            <Row key={g.slug} icon={<Compass className="w-4 h-4 text-[#34A853]" />}
              onClick={() => go(`/explore/${g.slug}`)}
              main={`${g.name} Travel Guide`} sub={g.place_type} />
          ))}
          {res.places?.length > 0 && <Section label="Places worldwide" />}
          {res.places?.map((p) => (
            <Row key={p.slug} icon={<Globe className="w-4 h-4 text-[#1a73e8]" />}
              onClick={() => go(`/explore/${p.slug}`)}
              main={p.name} sub={[p.kind === "state" ? "region" : "city", p.state !== p.name ? p.state : null, p.country].filter(Boolean).join(" · ")} />
          ))}
          {res.tips?.length > 0 && <Section label="Traveler tips" />}
          {res.tips?.map((t) => (
            <Row key={t.id} icon={<Lightbulb className="w-4 h-4 text-[#FBBC05]" />}
              onClick={() => go(`/explore/${t.slug}`)}
              main={t.title} sub={`${t.type} · community`} />
          ))}
          {res.articles?.length > 0 && <Section label="Articles & guides" />}
          {res.articles?.map((a) => (
            <Row key={a.slug} icon={<FileText className="w-4 h-4 text-primary" />}
              onClick={() => go(`/articles/${a.slug}`)}
              main={a.title} sub={a.category || "TravelerPulse Library"} />
          ))}
          {!res.airports?.length && !res.airlines?.length && !res.countries?.length &&
           !res.articles?.length && !res.destinations?.length && !res.pages?.length &&
           !res.guides?.length && !res.tips?.length && !res.places?.length && (
            <div className="px-4 py-6 text-sm text-muted-foreground">No matches found.</div>
          )}
        </div>
      )}
    </div>
  );
}

const Section = ({ label }) => (
  <div className="px-4 pt-3 pb-1 text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
);

const Row = ({ icon, main, sub, onClick }) => (
  <button onClick={onClick}
    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-primary hover:text-white transition-colors group">
    <span className="group-hover:text-white">{icon}</span>
    <span className="min-w-0">
      <span className="block text-sm font-medium truncate">{main}</span>
      <span className="block text-xs text-muted-foreground group-hover:text-white/80 truncate font-mono">{sub}</span>
    </span>
  </button>
);
